import React, { ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Flex, mq } from '@kvdbil/components';
import { translateRoute } from '~/App/shared/localization';
import { idToken as idTokenSelector } from '~/App/shared/selectors/sessionSelector';
import { requestErrorHandler } from '~/helpers/notifyError';
import { useHistory } from 'react-router';
import { useTranslation } from '~/Locale';
import { confirmUserSettings as confirmUserSettingsAction } from '~/App/shared/actions/session';
import { confirmUserSettings } from '~/helpers/orchestration/myPages';

const Container = styled.div`
  padding: 0.25rem 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
  background: ${({ theme }) => theme.colors.error.lighter};

  ${mq('tablet')} {
    padding: 0.5rem 2rem;
  }
`;
const Content = styled.div`
  max-width: 1024px;
  margin: auto;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export interface ConfirmUserSettingsMessageProps {
  message: ReactNode;
  confirmSettings?: typeof confirmUserSettings;
}
export const ConfirmUserSettingsMessage = ({
  message,
  confirmSettings = confirmUserSettings
}: ConfirmUserSettingsMessageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const idToken = useSelector(idTokenSelector);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);

    try {
      await confirmSettings({ idToken });
      dispatch(confirmUserSettingsAction());
    } catch (error: unknown) {
      requestErrorHandler(error);
    }

    setIsLoading(false);
  };

  const handleGotoSettings = async () => {
    await handleConfirm();

    history.push(
      translateRoute(t, '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SETTINGS')
    );
  };

  const handleConfirmClick = async () => {
    await handleConfirm();
  };

  return (
    <Container role="alert" aria-live="assertive">
      <Content>
        <span>{message}</span>
        <Flex gap={0} wrap="wrap">
          <Button
            onClick={handleGotoSettings}
            variant="flat"
            color="neutral"
            isLoading={isLoading}
          >
            {t('Settings')}
          </Button>
          <Button
            onClick={handleConfirmClick}
            variant="flat"
            color="neutral"
            isLoading={isLoading}
          >
            {t('Confirm')}
          </Button>
        </Flex>
      </Content>
    </Container>
  );
};

export default ConfirmUserSettingsMessage;
